import api from '~/utils/api';
import { sumPrice, sumTax } from '~/utils/compute';
import { toCurrency } from '~/utils/formatters';
import {
  isCreditClaim,
  pendingLineItemClaimToAutomationLineItem,
} from '~/utils/line-item-claim';
import { makeCrewLoaderWithLineItemClaims, redirect } from '~/utils/routing';

const defaultGiftCardTextContent =
  'Get a gift card for store credit. A redeemable code will be sent via email once your return has been approved.';

const defaultRefundTextContent =
  'Disclaimer: Receive a refund to your original payment method once your return has been approved. If you choose this method, the final amount may be subject to a return handling fee. The handling fee amount will be calculated for your review before you submit your request.';

export const { loader, useLoaderData: useRefundOptionsData } =
  makeCrewLoaderWithLineItemClaims(async ({ context }) => {
    const {
      storefrontId,
      theme: { offerGiftCardDetailText, offerRefundDetailText },
      return: {
        isRefundEnabled: isSettingsRefundEnabled,
        isGiftCardEnabled: isSettingsGiftCardEnabled,
      },
    } = context.settings;

    const { currencyCode } = context.order;

    const claims = context.lineItemClaims.filter(isCreditClaim);

    // * no monetary claims so skip to address
    if (claims.length === 0) {
      return redirect(`../address`);
    }

    // *  currently the claim doesn't support mixed monetary resolution methods
    // * so need to check that all line item claims support one method
    const isAllClaimsRefundable = claims.every(
      (claim) => claim.lineItem.return.refund.isEligible,
    );
    const isAllClaimsGiftCards = claims.every(
      (claim) => claim.lineItem.return.giftCard.isEligible,
    );

    const isRefundEnabled = isSettingsRefundEnabled && isAllClaimsRefundable;
    const isGiftCardEnabled = isSettingsGiftCardEnabled && isAllClaimsGiftCards;

    // * have monetary claims but fails to be eligible for refund either by settings or variation in the line item eligibility -- dead end at the moment
    // ? potential change when mixed monetary resolution methods are supported by the claim
    if (!isRefundEnabled && !isGiftCardEnabled) {
      // ! tightly coupled to the refund options error boundary - changes here should be reflected there
      return new Error(
        `RefundOptionsErrorBoundary: 
     User has monetary claims but fails to be eligible for credit either by settings or variation in each claim line item eligibility.`,
        {
          cause: { orderIdFromPlatform: context.order.idFromPlatform },
        },
      );
    }

    const refundSubtotal = sumPrice(claims);
    const taxRefundValue = sumTax(claims);
    const lineItems = context.lineItemClaims.map((claim) =>
      pendingLineItemClaimToAutomationLineItem(claim),
    );

    const { giftCardIncentiveAmount } = await api.checkBeforeClaimSubmission({
      params: { storefrontId },
      body: {
        kind: 'estimateIncentive' as const,
        lineItems,
      },
    });

    const incentiveValue =
      isGiftCardEnabled && giftCardIncentiveAmount ?
        giftCardIncentiveAmount
      : 0;
    const formatCurrency = toCurrency(currencyCode);

    const giftCardTextContent =
      offerGiftCardDetailText ?? defaultGiftCardTextContent;
    const refundTextContent = offerRefundDetailText ?? defaultRefundTextContent;

    return {
      isRefundEnabled,
      isGiftCardEnabled,
      hasIncentive: Boolean(giftCardIncentiveAmount),
      incentive: formatCurrency(incentiveValue),
      refund: formatCurrency(refundSubtotal + taxRefundValue),
      tax: formatCurrency(taxRefundValue),
      total: formatCurrency(refundSubtotal + taxRefundValue + incentiveValue),
      refundTextContent,
      giftCardTextContent,
    };
  });
